/**
 * One display status per product / SKU row on /products and /products/:id.
 *
 * Business rules:
 * - Draft, Active, Archived on Shopify and Deleted on Shopify are inherited,
 *   read only, from the FRESH Shopify product status (`shopifyStatus`: the
 *   lifecycle sweep's `product_status`, falling back to `products.status`
 *   when there is no lifecycle row). Print Run never writes it.
 * - Archived is owned by Print Run (`skus.retired_at`) and overrides every
 *   Shopify status.
 * - Archived on Shopify / Deleted on Shopify mark SKUs that are gone from
 *   Shopify but still live in Print Run: exactly the ones an operator will
 *   want to archive here.
 * - A POD blank sits behind many Shopify products, so it shows only Active or
 *   Archived; no single product's Shopify status describes it.
 * - An unknown or absent Shopify status shows NO badge, never a guess.
 */

export type SkuDisplayStatus =
  | 'active'
  | 'draft'
  | 'archived'
  | 'archived_on_shopify'
  | 'deleted_on_shopify';

/**
 * A blank base garment: POD, not locked, not pre-printed. `isLocked` must be
 * exactly false, so a NULL is never treated as a blank.
 *
 * KEEP-IN-LOCKSTEP with `isBlankSkuRow`
 * (apps/backend/src/modules/production-orders/blank-garment-name.ts).
 */
export function isPodBlank({
  productionType,
  isLocked,
  isPreprinted,
}: {
  productionType: string | null | undefined;
  isLocked: boolean | null | undefined;
  isPreprinted: boolean | null | undefined;
}): boolean {
  return productionType === 'POD' && isLocked === false && isPreprinted !== true;
}

/** Maps a Shopify product status to a display status; anything else is null. */
function fromShopifyStatus(shopifyStatus: string | null | undefined): SkuDisplayStatus | null {
  const normalized = typeof shopifyStatus === 'string' ? shopifyStatus.trim().toLowerCase() : '';
  switch (normalized) {
    case 'draft':
      return 'draft';
    case 'active':
      return 'active';
    case 'archived':
      return 'archived_on_shopify';
    case 'deleted':
      return 'deleted_on_shopify';
    default:
      return null;
  }
}

export function resolveSkuStatus({
  retiredAt,
  shopifyStatus,
  productionType,
  isLocked,
  isPreprinted,
}: {
  retiredAt?: string | null;
  shopifyStatus: string | null | undefined;
  productionType: string | null | undefined;
  isLocked: boolean | null | undefined;
  isPreprinted: boolean | null | undefined;
}): SkuDisplayStatus | null {
  if (retiredAt) return 'archived';
  if (isPodBlank({ productionType, isLocked, isPreprinted })) return 'active';
  return fromShopifyStatus(shopifyStatus);
}

export function resolveProductStatus({
  shopifyStatus,
  productionType,
  variants,
}: {
  shopifyStatus: string | null | undefined;
  productionType: string | null | undefined;
  variants: ReadonlyArray<{
    retiredAt?: string | null;
    productionType?: string | null;
    isLocked?: boolean | null;
    isPreprinted?: boolean | null;
  }>;
}): SkuDisplayStatus | null {
  if (variants.length > 0 && variants.every((v) => Boolean(v.retiredAt))) return 'archived';

  const live = variants.filter((v) => !v.retiredAt);
  if (
    productionType === 'POD' &&
    live.length > 0 &&
    live.every((v) =>
      isPodBlank({ productionType: v.productionType, isLocked: v.isLocked, isPreprinted: v.isPreprinted }),
    )
  ) {
    return 'active';
  }

  return fromShopifyStatus(shopifyStatus);
}

/** The usage guard's warning code; it reads the possibly stale products.status. */
const SHOPIFY_ACTIVE_WARNING = 'shopify_product_active';

/**
 * One rule for "is this SKU still live on Shopify", shared by the callout and
 * the per-SKU lines so they cannot drift from the row badge: live only when
 * the fresh `shopifyStatus` the badge shows is active.
 *
 * The usage guard's own `shopify_product_active` warning is ALWAYS dropped,
 * on purpose: `get_sku_usage` reads the stale `products.status`, so it would
 * contradict the badge (follow-up: have the guard read
 * `org_shopify_product_lifecycle_state`). Every other warning passes through
 * untouched.
 */
export function resolveShopifyLiveness(
  target: { shopifyStatus: string | null | undefined },
  guardWarnings: readonly string[],
): { shopifyLive: boolean; warnings: string[] } {
  return {
    shopifyLive: fromShopifyStatus(target.shopifyStatus) === 'active',
    warnings: guardWarnings.filter((w) => w !== SHOPIFY_ACTIVE_WARNING),
  };
}

/**
 * One SKU the Archive / Unarchive dialog acts on. `shopifyStatus` is the
 * fresh status the row badge uses (null when there is none); `retiredAt`
 * splits Archive targets (live) from Unarchive targets (ERP archived).
 */
export type ArchiveSkuTarget = {
  id: string;
  sku: string;
  shopifyStatus: string | null;
  retiredAt: string | null;
};

/** The one place an archive target is built; an ungrouped SKU has no product. */
export function toArchiveTarget(
  variant: { id: string; sku: string; retiredAt?: string | null },
  product?: { shopifyStatus?: string | null } | null,
): ArchiveSkuTarget {
  return {
    id: variant.id,
    sku: variant.sku,
    shopifyStatus: product?.shopifyStatus ?? null,
    retiredAt: variant.retiredAt ?? null,
  };
}

/** Live rows can be archived; ERP-archived rows can be unarchived. */
export function partitionByRetired<T extends { retiredAt?: string | null }>(
  rows: readonly T[],
): { live: T[]; archived: T[] } {
  const live: T[] = [];
  const archived: T[] = [];
  for (const row of rows) (row.retiredAt ? archived : live).push(row);
  return { live, archived };
}
